import React from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import {
  Expense,
  getExpenseStatusColor,
  getExpenseCategoryDisplay,
  getPaymentMethodDisplay,
  formatCurrency,
} from '../../api/expenses';
import { BrandModal, BrandModalBody, BrandModalFooter, BrandModalHeader } from '../ui/BrandModal';

interface ViewExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  expense: Expense | null;
  onEdit?: () => void;
  onDelete?: () => void;
}

const ViewExpenseModal: React.FC<ViewExpenseModalProps> = ({
  isOpen,
  onClose,
  expense,
  onEdit,
  onDelete,
}) => {
  if (!expense) return null;

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader title="Expense Details" subtitle={`ID: ${expense.id}`} onClose={onClose} />
      <BrandModalBody>
            <div className="mb-4 flex items-center justify-end space-x-2">
              {onEdit && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="inline-flex items-center p-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  className="inline-flex items-center p-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Status Badge */}
            <div className="mb-6">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getExpenseStatusColor(expense.status)}`}>
                {expense.status}
              </span>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Basic Info */}
              <div className="space-y-6">
                <div className="bg-slate-50/80 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-slate-900 mb-3">Expense Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-slate-700">Transaction Date:</span>
                      <p className="text-slate-900 mt-1">
                        {format(new Date(expense.transactionDate), 'dd MMM yyyy')}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-slate-700">Category:</span>
                      <p className="text-slate-900 mt-1">{getExpenseCategoryDisplay(expense.category)}</p>
                    </div>
                    <div>
                      <span className="font-medium text-slate-700">Payment Method:</span>
                      <p className="text-slate-900 mt-1">{getPaymentMethodDisplay(expense.paymentMethod)}</p>
                    </div>
                    <div>
                      <span className="font-medium text-slate-700">Status:</span>
                      <p className="text-slate-900 mt-1">{expense.status}</p>
                    </div>
                  </div>
                </div>

                {/* Optional Fields */}
                {(expense.receiptNumber || expense.vendorId) && (
                  <div className="bg-slate-50/80 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-slate-900 mb-3">Additional Information</h4>
                    <div className="grid grid-cols-1 gap-4 text-sm">
                      {expense.receiptNumber && (
                        <div>
                          <span className="font-medium text-slate-700">Receipt Number:</span>
                          <p className="text-slate-900 mt-1">{expense.receiptNumber}</p>
                        </div>
                      )}
                      {expense.vendorId && (
                        <div>
                          <span className="font-medium text-slate-700">Vendor ID:</span>
                          <p className="text-slate-900 mt-1">{expense.vendorId}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Description & Notes */}
              <div className="space-y-6">
                <div className="bg-slate-50/80 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-slate-900 mb-3">Description</h4>
                  <p className="text-slate-900 text-sm whitespace-pre-wrap">
                    {expense.description}
                  </p>
                </div>

                {expense.notes && (
                  <div className="bg-slate-50/80 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-slate-900 mb-3">Notes</h4>
                    <p className="text-slate-900 text-sm whitespace-pre-wrap">
                      {expense.notes}
                    </p>
                  </div>
                )}

                {/* Amount Display */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-red-900 mb-3">Total Amount</h4>
                  <div className="text-3xl font-bold text-red-600">
                    {formatCurrency(expense.amount)}
                  </div>
                </div>
              </div>
            </div>

            {/* Timestamps */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-500">
                <div>
                  <span className="font-medium">Created:</span> {format(new Date(expense.createdAt), 'dd MMM yyyy HH:mm')}
                </div>
                <div>
                  <span className="font-medium">Last Updated:</span> {format(new Date(expense.updatedAt), 'dd MMM yyyy HH:mm')}
                </div>
              </div>
            </div>
      </BrandModalBody>

      <BrandModalFooter>
        {onEdit && (
          <button type="button" onClick={onEdit} className="btn btn-primary text-sm">
            Edit Expense
          </button>
        )}
        <button type="button" onClick={onClose} className="btn btn-secondary text-sm">
          Close
        </button>
      </BrandModalFooter>
    </BrandModal>
  );
};

export default ViewExpenseModal;

 